import { useCallback } from 'react'

import { useModal } from '@circlefin/modal-router'
import { FullScreen } from '@modals/layout'
import { routes } from '@services/sections/modal'
import {
  ADDRESS_IS_NOT_ELIGIBLE,
  getError,
} from '@shared/apollo/lib/error/codes'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  BlockchainRecipientAddressesWithMetadataDocument,
  BlockchainRecipientAddressOwner,
  BlockchainRecipientAddressWithMetadataDocument,
  ProviderNetwork,
  useSetBlockchainRecipientAddressAdditionalInfoMutation,
  WalletType,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { getAddAddressModalPathByStatus } from '../../../utils'

import { AdditionalWalletInfoForm } from './AdditionalWalletInfo.Form'

import type { AdditionalWalletInfoFormProps } from './AdditionalWalletInfo.Form'

export interface AdditionalWalletInfoProps {
  /**
   * Address Id.
   */
  addressId?: string
  /**
   * Address Owner.
   */
  owner?: BlockchainRecipientAddressOwner
}

export const AdditionalWalletInfo: React.FC<AdditionalWalletInfoProps> = ({
  addressId,
  owner,
}) => {
  const { t } = useTranslation('address-book/modals')
  const modal = useModal()

  const [setAdditionalWalletInfo, { loading, error }] =
    useSetBlockchainRecipientAddressAdditionalInfoMutation({
      refetchQueries: [
        BlockchainRecipientAddressWithMetadataDocument,
        BlockchainRecipientAddressesWithMetadataDocument,
      ],
    })

  const handleSubmit: AdditionalWalletInfoFormProps['onSubmit'] = useCallback(
    (values) => {
      // Only pass vasp for hosted wallets
      const vasp =
        values.walletType === WalletType.HOSTED
          ? {
              id: values.vasp.id ?? '',
              name: values.vasp.name ?? '',
              network:
                values.vasp.network === ProviderNetwork.TRUST
                  ? ProviderNetwork.TRUST
                  : null,
            }
          : undefined

      void setAdditionalWalletInfo({
        variables: {
          input: {
            id: addressId ?? '',
            walletType: values.walletType,
            vasp,
          },
        },
        onCompleted: (data) => {
          modal.router.push({
            pathname: getAddAddressModalPathByStatus(
              data.setBlockchainRecipientAddressAdditionalInfo.state,
            ),
            query: {
              addressId,
            },
          })
        },
        onError: (error) => {
          const { graphQLErrors } = error

          if (getError(ADDRESS_IS_NOT_ELIGIBLE, graphQLErrors)) {
            return modal.router.push({
              pathname: routes.account.addressBook.addNewAddress.error,
            })
          }
        },
      })
    },
    [addressId, modal.router, setAdditionalWalletInfo],
  )

  return (
    <FullScreen>
      <GraphQLErrorBoundary error={error}>
        <div className="mb-6 flex flex-col items-center pb-8">
          <h2 className="type-h-section-md">
            {t('addAddress.additionalWalletInfo.title')}
          </h2>
          <h3 className="text-neutral-subtle type-body-base-bold">
            {/* TODO: Add subtitle once copy is finalized */}
          </h3>
        </div>
        <AdditionalWalletInfoForm
          loading={loading}
          onSubmit={handleSubmit}
          owner={owner ?? BlockchainRecipientAddressOwner.customer}
        />
      </GraphQLErrorBoundary>
    </FullScreen>
  )
}
